import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useUnifiedAuth } from '../../context/UnifiedAuthContext';
import { updateUserProfile, getCourse as getCourseFirestore } from '../../firebase/unifiedDataService';
// Note: Direct Firebase imports removed - using unifiedDataService and studentStatsService
import { CourseEnrollmentService } from '../../firebase/unifiedDataService';
import { getStudentStats, getRecentActivity } from '../../utils/studentStatsService';
import { getCourseQuestionStats, generatePracticeQuiz } from '../../utils/questionStatsService';
import { validateInviteCode } from '../../utils/courseInviteService';
import ViewLayout2 from '../../components/ui/ViewLayout2';
import PracticeSettingsModal from '../../components/student/PracticeSettingsModal';
import { Logger } from '../../utils/logger';
import { notifySuccess, notifyError, notifyWarning } from '../../services/notificationService';

const log = Logger.create('StudentDashboard');

/**
 * StudentDashboard - Main dashboard for registered students
 * Shows stats, enrolled courses, and recent activity
 * 
 * DATA SOURCES:
 * - Student Profile: Firestore users/{uid}
 * - Student Stats: Firestore student_stats/{uid}  
 * - Courses: Firestore courses/{id}
 * - Course Enrollments (teacher view): RTDB course_enrollments/{teacherId}/{courseId}
 */
function StudentDashboard() {
  const { user, userProfile, isAuthenticated, loading, logout } = useUnifiedAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [stats, setStats] = useState(null);
  const [courses, setCourses] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [joinCode, setJoinCode] = useState('');
  const [joinError, setJoinError] = useState('');
  const [isJoining, setIsJoining] = useState(false);
  const [quizCode, setQuizCode] = useState('');
  const [quizError, setQuizError] = useState('');
  
  // Practice mode state
  const [practiceModalOpen, setPracticeModalOpen] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [courseQuestionCounts, setCourseQuestionCounts] = useState({});
  
  // Pre-fill joinCode from URL if provided
  useEffect(() => {
    const codeFromUrl = searchParams.get('joinCode');
    if (codeFromUrl && !joinCode) {
      setJoinCode(codeFromUrl.toUpperCase());
    }
  }, [searchParams]);
  
  // Redirect if not authenticated
  useEffect(() => {
    if (!loading && !isAuthenticated) {
      navigate('/?auth=true');
    }
  }, [loading, isAuthenticated, navigate]);
  
  // Load student stats and recent activity (Firestore with auto-migration)
  useEffect(() => {
    if (!user) return;
    
    const loadStats = async () => {
      try {
        const studentStats = await getStudentStats(user.uid);
        setStats(studentStats);
        
        const activities = await getRecentActivity(user.uid, 10);
        setRecentActivity(activities);
      } catch (error) {
        log.error('Error loading student stats:', error);
      }
    };
    
    loadStats();
  }, [user]);
  
  // Load enrolled courses from Firestore
  useEffect(() => {
    const loadCourses = async () => {
      if (!userProfile?.enrolledCourses) {
        setCourses([]);
        return;
      }
      
      const enrollments = userProfile.enrolledCourses;
      const coursePromises = Object.values(enrollments)
        .filter(e => e.status === 'active')
        .map(async (enrollment) => {
          try {
            // Load course from Firestore (single source of truth)
            const course = await getCourseFirestore(enrollment.courseId);
            if (course) {
              return {
                ...course,
                teacherUserId: enrollment.teacherUserId,
                enrolledAt: enrollment.enrolledAt
              };
            }
            return null;
          } catch (err) {
            log.error('Error loading course:', err);
            return null;
          }
        });
      
      const loadedCourses = (await Promise.all(coursePromises)).filter(Boolean);
      setCourses(loadedCourses);
      
      // Load question counts for each course
      if (user && loadedCourses.length > 0) {
        const counts = {};
        await Promise.all(
          loadedCourses.map(async (course) => {
            try {
              const questionStats = await getCourseQuestionStats(user.uid, course.id);
              counts[course.id] = questionStats?.length || 0;
            } catch (err) {
              log.error('Error loading question count:', err);
              counts[course.id] = 0;
            }
          })
        );
        setCourseQuestionCounts(counts);
      }
    };
    
    loadCourses();
  }, [userProfile, user]);
  
  // Handle join course
  const handleJoinCourse = async (e) => {
    e.preventDefault();
    if (!joinCode.trim() || !user) return;
    
    setIsJoining(true);
    setJoinError('');
    
    try {
      // Validate invite code using Firestore
      const validation = await validateInviteCode(joinCode.trim());
      
      if (!validation.valid) {
        setJoinError(validation.error);
        return;
      }
      
      const invite = validation.invite;
      const now = Date.now();
      const enrollmentKey = `${invite.teacherUserId}_${invite.courseId}`;
      
      // Check if already enrolled (from userProfile)
      if (userProfile?.enrolledCourses?.[enrollmentKey]?.status === 'active') {
        setJoinError('Du bist bereits in diesem Kurs eingeschrieben.');
        return;
      }
      
      // 1. Add to student's enrolled courses in Firestore users/{uid}
      const enrollmentData = {
        [`enrolledCourses.${enrollmentKey}`]: {
          courseId: invite.courseId,
          teacherUserId: invite.teacherUserId,
          courseName: invite.courseName,
          enrolledAt: now,
          status: 'active',
          enrolledVia: 'invite',
          inviteCode: invite.code
        }
      };
      
      await updateUserProfile(user.uid, enrollmentData);
      log.success(`Enrolled student ${user.uid} in course ${invite.courseId} (Firestore)`);
      
      // 2. Add to course enrollments in RTDB using CourseEnrollmentService (for teacher live view)
      await CourseEnrollmentService.addEnrollment(
        invite.teacherUserId,
        invite.courseId,
        user.uid,
        {
          studentEmail: userProfile?.email || '',
          studentName: userProfile?.displayName || 'Unbekannt',
          enrolledVia: 'invite',
          inviteCode: invite.code
        }
      );
      
      log.success('Added to course enrollments (RTDB via CourseEnrollmentService)');
      
      // Success - clear form and reload page to refresh userProfile
      setJoinCode('');
      notifySuccess(`Erfolgreich dem Kurs "${invite.courseName}" beigetreten!`);
      window.location.reload();
      
    } catch (err) {
      log.error('Error joining course:', err);
      setJoinError('Ein Fehler ist aufgetreten. Bitte versuche es erneut.');
    } finally {
      setIsJoining(false);
    }
  };
  
  // Handle logout
  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (err) {
      log.error('Logout error:', err);
    }
  };
  
  // Handle join quiz
  const handleJoinQuiz = (e) => {
    e.preventDefault();
    if (!quizCode.trim()) return;
    
    setQuizError('');
    // Navigate directly to quiz play view
    navigate(`/play/${quizCode.trim()}`);
  };
  
  // Handle practice quiz
  const handleOpenPracticeModal = (course) => {
    setSelectedCourse(course);
    setPracticeModalOpen(true);
  };
  
  const handleStartPractice = async (settings) => {
    if (!selectedCourse || !user) return;
    
    try {
      // Get all question stats for this course
      const questionStats = await getCourseQuestionStats(user.uid, selectedCourse.id);
      
      if (!questionStats || questionStats.length === 0) {
        notifyWarning('Noch keine Fragen zum Üben verfügbar. Nimm zuerst an einem Quiz dieses Kurses teil.');
        return;
      }
      
      // Generate practice quiz using service
      const selectedQuestions = generatePracticeQuiz(questionStats, settings);
      
      if (selectedQuestions.length === 0) {
        notifyWarning('Keine passenden Fragen gefunden.');
        return;
      }
      
      // Navigate to practice view with questions
      // Store in sessionStorage for practice view to load
      sessionStorage.setItem('practiceQuizQuestions', JSON.stringify(selectedQuestions));
      sessionStorage.setItem('practiceQuizSettings', JSON.stringify({
        courseName: selectedCourse.name,
        courseId: selectedCourse.id,
        ...settings
      }));
      
      navigate('/student/practice');
    } catch (err) {
      log.error('Error starting practice:', err);
      notifyError('Fehler beim Starten des Übungsmodus');
    }
  };
  
  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-green-500 border-t-transparent mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-400">Laden...</p>
        </div>
      </div>
    );
  }
  
  // Calculate stats
  const displayStats = {
    quizzes: stats?.totalQuizzesTaken || 0,
    questions: stats?.totalQuestionsAnswered || 0,
    correctRate: stats?.totalQuestionsAnswered 
      ? Math.round((stats.totalCorrectAnswers / stats.totalQuestionsAnswered) * 100)
      : 0,
    streak: stats?.currentStreak || 0
  };
  
  return (
    <ViewLayout2
      title={userProfile?.displayName || 'Student'}
      maxWidth="7xl"
    >
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="text-2xl font-bold text-gray-900 dark:text-white">
              {displayStats.quizzes}
            </div>
            <div className="text-sm text-gray-500 dark:text-gray-400">Quizzes</div>
          </div>
          
          <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="text-2xl font-bold text-gray-900 dark:text-white">
              {displayStats.questions}
            </div>
            <div className="text-sm text-gray-500 dark:text-gray-400">Fragen</div>
          </div>
          
          <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="text-2xl font-bold text-green-600 dark:text-green-400">
              {displayStats.correctRate}%
            </div>
            <div className="text-sm text-gray-500 dark:text-gray-400">Richtig</div>
          </div>
          
          <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="text-2xl font-bold text-orange-600 dark:text-orange-400">
              {displayStats.streak}
            </div>
            <div className="text-sm text-gray-500 dark:text-gray-400">Streak</div>
          </div>
        </div>
        
        <div className="grid md:grid-cols-2 gap-8">
          {/* Enrolled Courses */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="p-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                <span>Deine Kurse</span>
              </h2>
            </div>
            
            {courses.length === 0 ? (
              <div className="p-8 text-center">
                <div className="text-4xl mb-4">
                  <svg className="w-16 h-16 mx-auto text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                  </svg>
                </div>
                <p className="text-gray-600 dark:text-gray-400 mb-2">
                  Du bist noch in keinem Kurs eingeschrieben.
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-500">
                  Nutze einen Einladungscode, um einem Kurs beizutreten.
                </p>
              </div>
            ) : (
              <div className="divide-y divide-gray-200 dark:divide-gray-700">
                {courses.map(course => {
                  const courseStats = stats?.courseStats?.[course.id];
                  const questionCount = courseQuestionCounts[course.id] || 0;
                  
                  return (
                    <div key={course.id} className="p-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 theme-transition">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex items-start gap-3 flex-1 min-w-0">
                          <div className="text-2xl">{course.icon || ''}</div>
                          <div className="flex-1 min-w-0">
                            <h3 className="font-medium text-gray-900 dark:text-white truncate">
                              {course.name}
                            </h3>
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                              {course.description || 'Keine Beschreibung'}
                            </p>
                            {courseStats && (
                              <div className="mt-2 flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400">
                                <span>{courseStats.quizzesTaken} Quizzes</span>
                                <span>Ø {Math.round(courseStats.averageScore)}%</span>
                                {questionCount > 0 && (
                                  <span>{questionCount} Fragen verfügbar</span>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                        
                        {/* Practice Button */}
                        <button
                          onClick={() => handleOpenPracticeModal(course)}
                          disabled={questionCount === 0}
                          className="flex items-center gap-2 px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded-lg theme-transition disabled:opacity-50 disabled:cursor-not-allowed"
                          title={questionCount === 0 ? 'Noch keine Fragen verfügbar' : 'Kurs üben'}
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                          </svg>
                          Üben
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          
          {/* Join Course */}
          <div>
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-6">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2 mb-4">
                <span>Kurs beitreten</span>
              </h2>
              
              <form onSubmit={handleJoinCourse}>
                {joinError && (
                  <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 text-red-700 dark:text-red-300 rounded-lg text-sm">
                    {joinError}
                  </div>
                )}
                
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={joinCode}
                    onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
                    placeholder="Einladungscode (z.B. ABC123)"
                    className="flex-1 px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white uppercase placeholder:normal-case"
                    maxLength={8}
                  />
                  <button
                    type="submit"
                    disabled={!joinCode.trim() || isJoining}
                    className="px-4 py-2.5 bg-green-600 hover:bg-green-700 text-white font-medium rounded-lg theme-transition disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isJoining ? '...' : 'Beitreten'}
                  </button>
                </div>
                
                <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                  Frage deine Dozent:in nach dem Einladungscode.
                </p>
              </form>
            </div>
            
            {/* Quick Quiz Join */}
            <div className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/30 dark:to-purple-900/30 rounded-xl p-6 border border-blue-200 dark:border-blue-700 mb-6">
              <h3 className="font-semibold text-blue-900 dark:text-blue-200 mb-2 flex items-center gap-2">
                <span>Quiz-Code eingeben</span>
              </h3>
              <p className="text-sm text-blue-700 dark:text-blue-300 mb-4">
                Hast du einen Quiz-Session-Code? Dann kannst du direkt teilnehmen.
              </p>
              
              <form onSubmit={handleJoinQuiz}>
                {quizError && (
                  <div className="mb-3 p-2 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 text-red-700 dark:text-red-300 rounded text-sm">
                    {quizError}
                  </div>
                )}
                
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={quizCode}
                    onChange={(e) => setQuizCode(e.target.value)}
                    placeholder="Quiz-Code (z.B. XYZ789)"
                    className="flex-1 px-4 py-2.5 border border-blue-300 dark:border-blue-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder:text-gray-500"
                  />
                  <button
                    type="submit"
                    disabled={!quizCode.trim()}
                    className="px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg theme-transition disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Starten →
                  </button>
                </div>
              </form>
            </div>
            
            {/* Quiz History Link */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
              <h3 className="font-semibold text-gray-900 dark:text-white mb-2 flex items-center gap-2">
                <span>Quiz-Historie</span>
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                Schau dir deine vergangenen Quizze an und übe sie erneut.
              </p>
              <Link
                to="/student/history"
                className="inline-block px-4 py-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 font-medium rounded-lg theme-transition"
              >
                Historie ansehen →
              </Link>
            </div>
          </div>
        </div>
        
        {/* Recent Activity */}
        {recentActivity.length > 0 && (
          <div className="mt-8 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="p-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                <span>Letzte Aktivitäten</span>
              </h2>
            </div>
            <div className="divide-y divide-gray-200 dark:divide-gray-700">
              {recentActivity.map((activity, index) => (
                <div key={index} className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-gray-900 dark:text-white">
                        {activity.quizName}
                      </p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {activity.courseName}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-green-600 dark:text-green-400">
                        {activity.score}%
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {new Date(activity.date).toLocaleDateString('de-DE')}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {/* Practice Settings Modal */}
        <PracticeSettingsModal
          isOpen={practiceModalOpen}
          onClose={() => setPracticeModalOpen(false)}
          onStartPractice={handleStartPractice}
          courses={courses}
          stats={courseQuestionCounts}
        />
    </ViewLayout2>
  );
}

export default StudentDashboard;
